import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminInvoiceCollectionItemResponse } from '../responses/admin-invoice-collection-item.response';
import { AdminInvoiceSettingsResponse } from '../responses/admin-invoice-settings.response';
import { AdminInvoiceSettingsUpdateRequest } from '../requests/admin-invoice-settings-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminInvoiceApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    cacheable?: boolean;
    request: CollectionRequest;
  }): Observable<CollectionResponse<AdminInvoiceCollectionItemResponse>> {
    const path = 'admin/invoice';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminInvoiceCollectionItemResponse,
          ),
        ),
      );
  }

  public downloadOrderInvoiceById(id: Guid): Observable<Blob> {
    const path = `admin/invoice/${id}/download`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.get(url, {
      context,
      responseType: 'blob',
    });
  }

  public downloadRefundInvoiceById(id: Guid): Observable<Blob> {
    const path = `admin/invoice/${id}/download`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.get(url, {
      context,
      responseType: 'blob',
    });
  }

  public synchronizeInvoiceById(id: Guid): Observable<void> {
    const path = `admin/invoice/${HttpParams.Id}/synchronize`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public sendToKsefById(id: Guid): Observable<void> {
    const path = `admin/invoice/${HttpParams.Id}/send-to-ksef`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchSettings(options: {
    skipByLoading?: boolean;
  }): Observable<AdminInvoiceSettingsResponse> {
    const path = 'admin/settings/invoice';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      {},
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminInvoiceSettingsResponse),
        ),
      );
  }

  public updateSettings(
    request: AdminInvoiceSettingsUpdateRequest,
  ): Observable<void> {
    const path = 'admin/settings/invoice';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      {},
    );
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminInvoiceSettingsUpdateRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
